import type { PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import { computeSpendPerTask } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { buildTrendBuckets, normalizeTrendBucketKey } from '@/domains/pages/ai-spend/utils/map-ai-spend-trend';
import { alignSeriesToBuckets } from '@/domains/pages/shared/chart-axis';
import { computeWorstBestEnvelope, inferWorstBestEnvelope } from '@/domains/pages/shared/segment-envelope';
import { formatTrendBucketLabel, type TrendQueryConfig } from '@/domains/pages/shared/trend-query';

export type SegmentSpendTrendMatrix = {
  labels: string[];
  totalSpendBySegment: Record<string, Array<number | null>>;
  spendPerTaskBySegment: Record<string, Array<number | null>>;
};

type SegmentTrendRows = {
  sourceBuckets: string[];
  totalSpend: Array<number | null>;
  spendPerTask: Array<number | null>;
};

export function mapSegmentSpendTrend(
  data: PaginatedExtendedGroupByQuery,
  trendQuery: TrendQueryConfig,
): SegmentSpendTrendMatrix {
  const buckets = buildTrendBuckets(trendQuery);
  const segmentRows: Record<string, SegmentTrendRows> = {};

  for (const row of data.data ?? []) {
    const segmentName = row.group_name_2?.trim();
    if (!segmentName) continue;

    if (!segmentRows[segmentName]) {
      segmentRows[segmentName] = { sourceBuckets: [], totalSpend: [], spendPerTask: [] };
    }

    const entry = segmentRows[segmentName];
    const bucketKey = normalizeTrendBucketKey(row.group_name ?? '', trendQuery.granularity);
    const cost = row.cost ?? null;
    const tasks = row.n_segments ?? 0;

    entry.sourceBuckets.push(bucketKey);
    entry.totalSpend.push(cost);
    entry.spendPerTask.push(computeSpendPerTask(cost, tasks));
  }

  const totalSpendBySegment: Record<string, Array<number | null>> = {};
  const spendPerTaskBySegment: Record<string, Array<number | null>> = {};

  for (const [name, entry] of Object.entries(segmentRows)) {
    totalSpendBySegment[name] = alignSeriesToBuckets(buckets, entry.sourceBuckets, entry.totalSpend);
    spendPerTaskBySegment[name] = alignSeriesToBuckets(buckets, entry.sourceBuckets, entry.spendPerTask);
  }

  const labels = buckets.map((bucket, index) =>
    formatTrendBucketLabel(bucket, trendQuery.granularity, trendQuery.bucketKind, index === buckets.length - 1),
  );

  return { labels, totalSpendBySegment, spendPerTaskBySegment };
}

export type SpendPeerBandPoint = { cheapest: number | null; mostExpensive: number | null };

/** Per-period min/max of spend per task across every segment, gaps interpolated. */
export function buildSpendPeerBand(
  spendPerTaskBySegment: Record<string, Array<number | null>>,
  bucketCount: number,
): SpendPeerBandPoint[] {
  return inferWorstBestEnvelope(computeWorstBestEnvelope(spendPerTaskBySegment, bucketCount)).map(
    ({ worst, best }) => ({
      cheapest: worst,
      mostExpensive: best,
    }),
  );
}
